import { useMemo } from 'react'
import type { ScheduleItem } from '../../types/index.js'
import {
  broadcastDayHours, broadcastDayKey, broadcastDayWindow, broadcastOffsetHours,
} from '../../lib/broadcast-day.js'
import { useServerClock } from '../../hooks/useServerClock.js'

// Configuration de la timeline.
export const TIMELINE_START_HOUR = 0
export const TIMELINE_END_HOUR = 24
/**
 * Hauteur nominale d'une journee, en heures. Les deux dimanches de changement
 * d'heure font exception : la timeline se dimensionne alors sur la duree reelle
 * du jour affiche, via `timelineHours`.
 */
export const TIMELINE_HOURS = TIMELINE_END_HOUR - TIMELINE_START_HOUR

/**
 * Duree de la journee affichee : 24 h, sauf le dimanche de passage a l'heure
 * d'ete (23 h) et celui du retour a l'heure d'hiver (25 h). Dessiner 24 bandes
 * ces jours-la faisait mentir la grille — une heure fantome au printemps, deux
 * programmes empiles sur la meme bande a l'automne.
 */
export function timelineHours(date: string): number {
  return broadcastDayHours(date)
}

/**
 * Hauteur affichee, en heures : la journee, prolongee de sa queue de nuit
 * quand `displayEndMs` depasse minuit d'antenne (`lib/night-tail.ts`).
 */
export function displayHours(date: string, displayEndMs?: number | null): number {
  const dayHours = timelineHours(date)
  if (!displayEndMs) return dayHours
  const tailHours = (displayEndMs - broadcastDayWindow(date).to.getTime()) / 3_600_000
  return tailHours > 0 ? dayHours + tailHours : dayHours
}

/**
 * Position verticale d'un instant dans la journee affichee.
 *
 * La mesure est le temps ecoule depuis minuit d'antenne, pas l'heure d'horloge :
 * elle reste continue et croissante le 25 octobre, ou 02:00 revient une seconde
 * fois. Elle vaut aussi hors de la journee — negative pour ce qui a commence la
 * veille, au-dela de la hauteur pour ce qui deborde sur le lendemain — ce dont
 * se sert le decoupage a minuit.
 */
export function dayY(iso: string | Date, date: string, pxPerHour: number): number {
  return Math.round(broadcastOffsetHours(iso, date) * pxPerHour)
}

/**
 * Echelle de la timeline, en pixels par minute. Valeur continue : c'est la
 * seule grandeur qui pilote la geometrie, le curseur de la barre d'outils
 * l'ecrit directement.
 *
 * La plage va de la journee entiere (1 px/min = 60 px/h) a la seconde pres
 * (240 px/min = 14400 px/h). Le haut de la plage sert a la regie pub : un spot
 * de 10 s doit rester manipulable.
 */
export const PPM_MIN = 1
export const PPM_MAX = 240
export const PPM_DEFAULT = 4.5

/**
 * Le curseur est logarithmique. Sur une echelle lineaire, tout le travail
 * courant de grille (1 a 12 px/min) tiendrait dans les 5 premiers pourcents de
 * la piste et serait inreglable.
 */
/**
 * La semaine a sa propre echelle, bien plus resserree : sept colonnes cote a
 * cote, on cherche la forme generale de la grille, pas la seconde. Partager
 * l'echelle du jour donnerait soit une semaine illisible, soit un jour inutile.
 */
export const PPMW_MIN = 1
/**
 * 60 px/min, soit un pixel par seconde. Le plafond precedent (6) laissait un
 * jingle de 20 s a 2 px : il tombait sous le plancher de hauteur et se lisait
 * comme un marqueur, sans qu'on puisse jamais le resoudre. A 60 il mesure
 * 20 px et redevient un bloc a part entiere.
 */
export const PPMW_MAX = 60
export const PPMW_DEFAULT = 2.2

export function clampPpmw(ppm: number): number {
  if (!Number.isFinite(ppm)) return PPMW_DEFAULT
  return Math.min(PPMW_MAX, Math.max(PPMW_MIN, ppm))
}

export const SLIDER_MIN = 0
export const SLIDER_MAX = 1000

const LOG_RATIO = Math.log(PPM_MAX / PPM_MIN)

export function sliderToPpm(position: number, min = PPM_MIN, max = PPM_MAX): number {
  const clamped = Math.min(SLIDER_MAX, Math.max(SLIDER_MIN, position))
  const ratio = Math.log(max / min)
  const raw = min * Math.exp((clamped / SLIDER_MAX) * ratio)
  // Une decimale en bas de plage, entier au-dela : evite un libelle qui change
  // a chaque pixel de curseur.
  return raw < 10 ? Math.round(raw * 10) / 10 : Math.round(raw)
}

export function ppmToSlider(ppm: number, min = PPM_MIN, max = PPM_MAX): number {
  const clamped = Math.min(max, Math.max(min, ppm))
  const ratio = Math.log(max / min)
  return Math.round((Math.log(clamped / min) / ratio) * SLIDER_MAX)
}

export function clampPpm(ppm: number): number {
  if (!Number.isFinite(ppm)) return PPM_DEFAULT
  return Math.min(PPM_MAX, Math.max(PPM_MIN, ppm))
}

/** Libelle du curseur : « 4,5 px/min », separateur decimal francais. */
export function formatPpm(ppm: number): string {
  const value = ppm < 10 ? ppm.toFixed(1).replace('.', ',') : String(Math.round(ppm))
  return `${value} px/min`
}

/**
 * Precision d'accrochage au deplacement, deduite de l'echelle : inutile de
 * proposer la seconde quand une minute mesure un pixel.
 */
export function snapMinutesForPpm(ppm: number): number {
  if (ppm >= 240) return 1 / 60   // 1 s
  if (ppm >= 120) return 1 / 30   // 2 s
  if (ppm >= 60)  return 1 / 6    // 10 s
  if (ppm >= 12)  return 0.5      // 30 s
  if (ppm >= 4)   return 1        // 1 min
  return 5                        // 5 min
}

/**
 * Densite de graduation deduite de l'echelle. Partagee par l'agenda et la
 * semaine type : a hauteur d'heure egale, les deux grilles doivent porter la
 * meme trame, sans quoi passer de l'une a l'autre demande de relire l'axe.
 */
export function timelineSteps(pxPerHour: number): {
  /** Minutes entre deux etiquettes chiffrees. */
  labelEvery: number
  /** Minutes entre deux graduations de l'axe. */
  tickEvery: number
  /** Minutes entre deux filets de fond dans les colonnes. */
  minorEvery: number
} {
  if (pxPerHour >= 3600) return { labelEvery: 1, tickEvery: 0.5, minorEvery: 1 }
  if (pxPerHour >= 720)  return { labelEvery: 5, tickEvery: 1, minorEvery: 5 }
  if (pxPerHour >= 240)  return { labelEvery: 15, tickEvery: 5, minorEvery: 15 }
  return { labelEvery: 60, tickEvery: 30, minorEvery: 30 }
}

export const TIME_COL_WIDTH = 70
export const ITEM_PADDING = 6

/**
 * Seuil de lisibilite : en dessous, l'element ne peut plus porter son propre
 * texte et part dans la gouttiere des liaisons.
 *
 * Le seuil est le meme a toutes les echelles, et il est pose au plus bas que le
 * texte supporte : 10 px, la hauteur du rendu `is-tiny` d'`AgendaBlock` — une
 * ligne en caption, interligne 1, sans marge. Un bloc de 10 px montre donc
 * encore son titre, tronque mais lu.
 *
 * Il a d'abord dependu de l'echelle, pour eviter qu'une vue d'ensemble ne se
 * remplisse de titres illisibles. Ca ne se justifie plus : le taquet de la
 * gouttiere porte la couleur de type, donc rien ne se perd a n'y envoyer que ce
 * qui ne pouvait de toute facon pas s'ecrire.
 */
export function minBlockHeightForPpm(_pxPerMinute: number): number {
  return 10
}

/**
 * Hauteur minimale d'un tiret dans la gouttiere. Il ne porte pas de texte, mais
 * il doit rester visible et surtout saisissable a la souris — c'est par lui
 * qu'on deplace un jingle.
 */
export const MARKER_TICK_HEIGHT = 8

/** Largeur de la gouttiere des liaisons, en pixels. */
export const GUTTER_WIDTH = 22

/**
 * Nombre de couloirs de la gouttiere. Deux suffisent : le cas courant est un
 * jingle suivi d'un infoservice, et empiler au-dela reviendrait a recreer dans
 * la gouttiere le chevauchement qu'on en a chasse.
 */
export const GUTTER_LANES = 2

export interface LayoutItem {
  item:      ScheduleItem
  top:       number
  naturalHeight: number
  height:    number
  bottom:    number
  left:      number
  width:     number
  column:    number
  totalCols: number
  /** Trop court pour porter son texte : rendu en tiret dans la gouttiere. */
  isMarker:  boolean
  /** Couloir de la gouttiere. Nul hors marqueur. */
  lane:      number
}

export interface ColumnSlot {
  column:    number
  totalCols: number
  left:      number
  width:     number
}

/**
 * Repartit en colonnes les elements qui se disputent reellement la meme plage
 * horaire. Un element seul occupe toute la largeur.
 *
 * Le partage se decide sur les horaires, jamais sur la geometrie. Le pixel ment
 * deux fois : `top` est arrondi a l'entier alors que la hauteur ne l'est pas, et
 * la hauteur d'affichage est plancheree. Deux elements bout a bout — l'un finit
 * a l'instant ou l'autre commence — se recouvraient ainsi d'une fraction de
 * pixel, et la grille les posait en demi-largeur cote a cote alors qu'ils ne se
 * disputent rien.
 *
 * Les marqueurs sont exclus : ils ne sont plus dans la colonne du tout, mais
 * dans la gouttiere. Les faire entrer dans le partage reduirait de moitie un
 * programme d'une heure a cause d'un jingle de 40 s pose sur sa frontiere.
 */
export function computeColumns(
  items: Array<{ startMs: number; endMs: number; isMarker: boolean }>,
): ColumnSlot[] {
  const slots: ColumnSlot[] = items.map(() => ({ column: 0, totalCols: 1, left: 0, width: 100 }))

  const solid = items
    .map((it, index) => ({ ...it, index }))
    .filter(it => !it.isMarker)
    .sort((a, b) => a.startMs - b.startMs)

  let cluster: typeof solid = []
  let clusterEnd = -Infinity

  const flush = () => {
    if (cluster.length > 1) {
      const cols = cluster.length
      cluster.forEach((it, colIdx) => {
        slots[it.index] = {
          column:    colIdx,
          totalCols: cols,
          width:     100 / cols,
          left:      (colIdx / cols) * 100,
        }
      })
    }
    cluster = []
  }

  for (const it of solid) {
    // `>=` : un element qui commence exactement a la fin du precedent ouvre un
    // nouveau groupe, il ne partage pas la largeur.
    if (cluster.length > 0 && it.startMs >= clusterEnd) flush()
    cluster.push(it)
    clusterEnd = Math.max(clusterEnd, it.endMs)
  }
  flush()

  return slots
}

/**
 * Repartit les tirets de la gouttiere en couloirs pour qu'ils ne se recouvrent
 * pas. Chaque tiret prend le premier couloir libre a sa hauteur ; au-dela de
 * `laneCount` on recycle le couloir le plus ancien — deux liaisons empilees au
 * pixel pres restent rares, et le pire cas reste confine a la gouttiere.
 *
 * Les tirets sont supposes tries par `top` croissant.
 */
export function assignMarkerLanes(
  markers: Array<{ top: number; height: number }>,
  laneCount = GUTTER_LANES,
): number[] {
  const laneBottoms = new Array<number>(laneCount).fill(-Infinity)

  return markers.map(marker => {
    const free = laneBottoms.findIndex(bottom => bottom <= marker.top)
    // Aucun couloir libre : celui qui se libere le plus tot fait le moins de
    // degats. `reduce` plutot qu'un tri, la liste fait deux entrees.
    const lane = free >= 0
      ? free
      : laneBottoms.reduce((best, bottom, index) => bottom < laneBottoms[best] ? index : best, 0)
    laneBottoms[lane] = marker.top + marker.height
    return lane
  })
}

function computeNowLineY(date: string, pxPerHour: number, now: Date): number | null {
  // Le jour d'antenne, pas le jour du poste : passe minuit a Paris, la ligne
  // MAINTENANT appartient deja a la journee suivante.
  if (broadcastDayKey(now) !== date) return null
  return dayY(now, date, pxPerHour)
}

/**
 * Calcule la position et les dimensions de chaque item sur la timeline.
 * La ligne "maintenant" utilise l'horloge serveur, car le backend est la
 * reference qui pilote CasparCG.
 */
export function useAgendaLayout(
  items: ScheduleItem[],
  pxPerMinute: number,
  date: string,
  conflicts: string[],
  /**
   * Fin de l'affichage quand la journée se prolonge sur la nuit
   * (`lib/night-tail.ts`). Absente ou avant minuit : la journée seule.
   */
  displayEndMs?: number | null,
): {
  layoutItems: LayoutItem[]
  totalHeight: number
  nowLineY: number | null
  conflictSet: Set<string>
  pxPerHour: number
} {
  const pxPerHour = clampPpm(pxPerMinute) * 60
  const { now } = useServerClock()
  const nowLineY = useMemo(() => computeNowLineY(date, pxPerHour, now), [date, pxPerHour, now])

  const layout = useMemo(() => {
    const totalHeight = displayHours(date, displayEndMs) * pxPerHour
    const conflictSet = new Set(conflicts)

    const sorted = [...items].sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    )

    // Journee demi-ouverte : minuit inclus, minuit du lendemain exclu. Ce qui
    // deborde de part et d'autre est coupe a la frontiere — un film commence la
    // veille se lit de 00:00 a sa fin, pas replie a son heure de depart.
    // La fin d'affichage peut dépasser minuit : la queue de nuit se dessine
    // sous la journée, jusqu'au premier élément du lendemain.
    const { from: dayStart, to: dayTo } = broadcastDayWindow(date)
    const dayEnd = displayEndMs && displayEndMs > dayTo.getTime() ? new Date(displayEndMs) : dayTo

    const minHeight = minBlockHeightForPpm(pxPerHour / 60)

    const positioned = sorted.filter(item => new Date(item.startTime) < dayEnd).map(item => {
      const itemStart = new Date(item.startTime)
      const itemEnd = new Date(item.endTime)
      const visualStart = itemStart < dayStart ? dayStart : itemStart
      const visualEnd = itemEnd > dayEnd ? dayEnd : itemEnd
      const top = dayY(visualStart, date, pxPerHour)
      const effectiveDurationSec = Math.max(0, (visualEnd.getTime() - visualStart.getTime()) / 1000)
      const naturalHeight = (effectiveDurationSec / 3600) * pxPerHour
      const isMarker = naturalHeight < minHeight
      // Un marqueur ne gonfle plus : il quitte la colonne pour la gouttiere, ou
      // son seul plancher est celui de la prise a la souris.
      const height = Math.max(naturalHeight, isMarker ? MARKER_TICK_HEIGHT : minHeight)
      return {
        item, top, naturalHeight, height,
        bottom: top + height,
        isMarker,
        startMs: visualStart.getTime(),
        endMs: visualEnd.getTime(),
      }
    })

    const layoutItems: LayoutItem[] = positioned.map(p => ({
      ...p,
      left: 0,
      width: 100,
      column: 0,
      totalCols: 1,
      lane: 0,
    }))

    computeColumns(positioned).forEach((slot, index) => {
      Object.assign(layoutItems[index], slot)
    })

    // `sorted` etant croissant, les marqueurs le sont aussi : c'est l'ordre
    // qu'attend la repartition en couloirs.
    const markerIndexes = layoutItems.map((li, index) => ({ li, index })).filter(entry => entry.li.isMarker)
    assignMarkerLanes(markerIndexes.map(entry => entry.li)).forEach((lane, position) => {
      layoutItems[markerIndexes[position].index].lane = lane
    })

    return { layoutItems, totalHeight, conflictSet, pxPerHour }
  }, [items, date, conflicts, pxPerHour, displayEndMs])

  return { ...layout, nowLineY }
}

// ─── Depose d'un element deplace ──────────────────────────────────────────────
//
// Tout ce qui suit raisonne en millisecondes, jamais en pixels. Le pixel est
// une projection lossy des horaires : `dayY` arrondit `top` a l'entier, si bien
// qu'a 1 px/min un demi-pixel vaut trente secondes. Resoudre la depose sur cette
// geometrie faisait atterrir un jingle *dans* le programme voisin — visuellement
// colle, reellement chevauchant, et le backend accepte le deplacement en se
// contentant de signaler le conflit. Le pixel ne sert donc plus qu'a une chose :
// convertir le geste de la souris en duree.

/** Distance sous laquelle un bord voisin attire l'element deplace, en pixels. */
export const SNAP_THRESHOLD_PX = 15

/**
 * Plafond de l'aimantage, en secondes. Le seuil est d'abord une distance a
 * l'ecran — l'operateur vise un bord qu'il voit — mais dezoome a fond, quinze
 * pixels valent un quart d'heure : la grille refusait alors de poser quoi que
 * ce soit a moins d'un quart d'heure d'un programme, en le collant d'office.
 */
export const MAGNET_MAX_SEC = 300

/** Portee de l'aimantage a l'echelle courante, en secondes. */
export function magnetSecForPpm(pxPerMinute: number): number {
  return Math.min(MAGNET_MAX_SEC, SNAP_THRESHOLD_PX / clampPpm(pxPerMinute) * 60)
}

/** Bornes reelles d'un voisin, telles que la base les porte. */
export interface DropNeighbour {
  id:      string
  startMs: number
  endMs:   number
}

/**
 * Aimante le depart — ou la fin — de l'element deplace sur le bord d'un voisin
 * situe a moins de `magnetMs`. Retourne le depart aimante, ou `null` si aucun
 * bord n'est assez proche.
 */
export function snapMsToNeighbours(
  rawStartMs: number,
  durationMs: number,
  neighbours: DropNeighbour[],
  magnetMs: number,
): number | null {
  const rawEndMs = rawStartMs + durationMs
  let best: number | null = null
  let minDiff = magnetMs

  for (const candidate of neighbours) {
    const startToEnd = Math.abs(rawStartMs - candidate.endMs)
    if (startToEnd < minDiff) {
      minDiff = startToEnd
      best = candidate.endMs
    }

    const endToStart = Math.abs(rawEndMs - candidate.startMs)
    if (endToStart < minDiff) {
      minDiff = endToStart
      best = candidate.startMs - durationMs
    }
  }

  return best
}

/** Voisins reellement mordus par un element pose a `startMs`. */
function hitsAt(startMs: number, durationMs: number, neighbours: DropNeighbour[]): DropNeighbour[] {
  // Comparaisons strictes : deux elements bout a bout, l'un finissant a
  // l'instant ou l'autre commence, ne se disputent rien.
  return neighbours.filter(n => startMs < n.endMs && n.startMs < startMs + durationMs)
}

/**
 * Horaire de depart d'un element lache sur la grille, en millisecondes.
 *
 * Deux regles, dans cet ordre :
 *   1. *aimantage* — si un bord voisin est a moins de `magnetMs`, on s'y colle
 *      exactement ; sinon on retombe sur le pas de la grille ;
 *   2. *non-recouvrement* — un element ne se pose jamais sur un autre. Si la
 *      cible mord sur un programme, elle est reportee sur le bord libre le plus
 *      proche (juste avant, ou juste apres le programme heurte).
 *
 * Retourne `null` quand aucune place libre n'existe a proximite : le
 * deplacement doit alors etre refuse plutot que de creer un conflit.
 */
export function resolveDropStartMs(
  rawStartMs: number,
  durationMs: number,
  neighbours: DropNeighbour[],
  dayStartMs: number,
  snapMinutes: number,
  magnetMs: number,
): number | null {
  // Le pas se compte depuis minuit d'antenne, pas depuis l'epoch : c'est la
  // seule origine qui fasse tomber l'accrochage sur des heures rondes.
  const snapMs = Math.max(1_000, Math.round(snapMinutes * 60_000))
  const gridMs = dayStartMs + Math.round((rawStartMs - dayStartMs) / snapMs) * snapMs

  const target = Math.max(
    dayStartMs,
    snapMsToNeighbours(rawStartMs, durationMs, neighbours, magnetMs) ?? gridMs,
  )

  const hit = hitsAt(target, durationMs, neighbours)
  if (hit.length === 0) return target

  // On ne retient que les bords des programmes *heurtes* : l'element se colle
  // juste avant ou juste apres celui sur lequel on l'a lache. Balayer tous les
  // voisins ferait sauter un element lache sur un apres-midi plein jusqu'a la
  // fin de journee, ce qui ne ressemble plus a un aimantage.
  const free = hit
    .flatMap(n => [n.startMs - durationMs, n.endMs])
    .filter(ms => ms >= dayStartMs && hitsAt(ms, durationMs, neighbours).length === 0)
    .sort((a, b) => Math.abs(a - rawStartMs) - Math.abs(b - rawStartMs))

  return free.length > 0 ? free[0] : null
}

// ─── Ancrage du defilement au changement d'echelle ───────────────────────────

/**
 * Position de defilement qui garde sous les yeux, apres un changement
 * d'echelle, l'instant qui s'y trouvait avant.
 *
 * `anchorRatio` designe la hauteur de reference dans la fenetre : 0,5 pour son
 * centre — ce que fait le curseur de la barre d'outils, qui ne designe aucun
 * point — la position du pointeur pour un zoom a la molette.
 *
 * `previousScrollTop` doit avoir ete releve *avant* le changement de hauteur.
 * Relire `scrollTop` apres coup ne marche pas : en dezoomant, la timeline
 * raccourcit et le navigateur ecrete le defilement avant meme que React ne
 * rende la main — l'ancre etait alors perdue, et chaque cran de zoom faisait
 * remonter la vue de quelques heures.
 */
export function scrollTopForZoom(
  previousScrollTop: number,
  viewportHeight: number,
  oldPxPerHour: number,
  newPxPerHour: number,
  totalHeight: number,
  anchorRatio = 0.5,
): number {
  const anchorPx = previousScrollTop + viewportHeight * anchorRatio
  const anchorHours = anchorPx / oldPxPerHour
  const wanted = anchorHours * newPxPerHour - viewportHeight * anchorRatio
  return Math.min(Math.max(0, totalHeight - viewportHeight), Math.max(0, wanted))
}
